import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { PageBlocksModel } from '~/models/PageBlock/PageBlocksModel';

/*
 * Хранилище блоков страницы (Pinia-store)
 *
 * Отвечает за:
 * - Хранение текущей модели блоков страницы
 * - Предоставление вычисляемых свойств для доступа к блокам страницы
 * - Проверку наличия данных по блокам страницы
 * - Установку и очистку данных блоков страницы
 *
 * @function usePageBlocksStore
 * @returns {{
 *   pageBlocksItem: import('vue').Ref<PageBlocksModel | null>;
 *   pageBlocks: import('vue').ComputedRef<PageBlocksModel | null>;
 *   hasPageBlocks: import('vue').ComputedRef<boolean>;
 *   setPageBlocks: (data: PageBlocksModel) => void;
 *   clearPageBlocks: () => void;
 * }}
 *
 * Методы и свойства:
 * - pageBlocksItem - реактивное состояние с текущей моделью блоков страницы или null
 * - pageBlocks - вычисляемое свойство для удобного доступа к текущей модели блоков страницы
 * - hasPageBlocks - флаг наличия данных по блокам страницы
 * - setPageBlocks - устанавливает новую модель блоков страницы
 * - clearPageBlocks - очищает данные блоков страницы (устанавливает null)
 */
export const usePageBlocksStore = defineStore('usePageBlocksStore', () => {
  /*
   * Текущее состояние с моделью блоков страницы
   * @type {import('vue').Ref<PageBlocksModel | null>}
   */
  const pageBlocksItem = ref<PageBlocksModel | null>(new PageBlocksModel());

  /*
   * Вычисляемое свойство для доступа к текущей модели блоков страницы
   * @type {import('vue').ComputedRef<PageBlocksModel | null>}
   */
  const pageBlocks = computed(() => pageBlocksItem.value);

  /*
   * Признак наличия данных по блокам страницы
   * @type {import('vue').ComputedRef<boolean>}
   */
  const hasPageBlocks = computed(() => pageBlocksItem.value !== null);

  /*
   * Устанавливает новую модель блоков страницы
   * @param {PageBlocksModel} data - данные для инициализации модели блоков страницы
   * @returns {void}
   */
  function setPageBlocks(data: PageBlocksModel): void {
    pageBlocksItem.value = new PageBlocksModel(data);
  }

  /*
   * Очищает данные блоков страницы (устанавливает значение в null)
   * @returns {void}
   */
  function clearPageBlocks(): void {
    pageBlocksItem.value = null;
  }

  return {
    pageBlocksItem,
    pageBlocks,
    hasPageBlocks,
    setPageBlocks,
    clearPageBlocks,
  };
});
